import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Button, Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft /* , faCopy */ } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faEdit, faRedo, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useLocation, matchPath } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../template/store';
import useDataCountry from '../../../hooks/useDataCountry';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import { updateDashbaordRefresh } from '../../common/components/DataTable/slices/DataTable';
import {
    submitInvoiceRequest,
    getInvoiceRequestTypeOfDocumentList,
    getInvoiceRequestCustomsClearanceChannelList,
    getInvoiceRequestPaymentModeList,
    getInvoiceRequestDeliveryZoneList,
    getInvoiceRequestModeOfTransportList,
    getInvoiceRequestInvChangeTypeList,
    // getInvoiceRequestCountryAreaCodeList,
    getInvoiceRequestDeliveryLocationList,
    getInvoiceRequestDetails,
    approveRejectInvoiceRequest,
    validateBlNumber,
    getInvoiceRequestCustomerAttConf,
    getTypeofTrailerList,
} from './InvoiceRequestActions';
import InvoiceRequestCustomerDocuments from './InvoiceRequestCustomerDocuments';
import InvoiceRequestContainerDetails from './InvoiceRequestContainerDetails';
import {
    clearInvoiceRequestViewAndCustomerState,
    resetInvoiceTypeList,
    updateCustomerAttData,
    updateInvoiceType,
    updateSelectedCustomerAttachmentFilePath,
    updateSelectedCustomerAttachmentName,
    updateSelectedCustomerFileServerPath,
    clearValidatedBlDetails,
    clearCustomerFields,
} from './InvoiceRequestSlice';
import { AESDecrypt } from '../../../encrypt-util';
import useBlCountry from '../../../hooks/useBlCountry';
import useInvoiceRequestBlCountry from '../../../hooks/useInvoiceRequestBlCountry';
import { getUnprefixedPathname } from '../../../helpers/utils';
import { getInstructionList, getOBLForInvoiceRequest, getOBLForOverStay } from '../dashboard/actions/instruction';
import {
    clearDocumentHistory,
    updateSelectedDocIdDH,
    updateSelectedEventIdDH,
} from '../blDetails/slices/documentHistorySlice';
import {
    DEMURRAGE_DETENTION_INVOICE_TYPE_CODE,
    INVOICE_REQUEST_STATUS_LABEL,
} from '../../constants/InvoiceRequestConstant';
import {
    clearData,
    clearTableInputValues,
    updateSelectedDeliveryZone,
} from './InvoiceRequestContainerSlice';
import moment from 'moment';

type MstOption = {
    value: number | string;
    label: string;
};

type FormValues = {
    blNumber: string;
    // Customer
    typeOfDocument: string;
    customsClearanceChannel: string;
    partyToInvoice: string;
    paymentMode: string;
    emailAddress: string;
    // countryAreaCode: string;
    contactNumber: string;
    remarks: string;
    // Liner
    deliveryLocation: string;
    linerRemarks: string;
    actionType: string;
};


const validationSchema = Yup.object({
    blNumber: Yup.string().trim().required('B/L No. is required'),
    // Customer
    typeOfDocument: Yup.string()
        .trim()
        .required('Type of Document is required'),
    customsClearanceChannel: Yup.string().required('Customs Clearance Channel is required'),
    partyToInvoice: Yup.string()
        .trim()
        .required('Party To Invoice is required'),
    paymentMode: Yup.string().required('Payment Mode on Invoice Form is required'),
    emailAddress: Yup.string()
        .trim()
        .required('Email Address is required')
        .email('Enter a valid email address'),
    
    contactNumber: Yup.string()
        .trim()
        .optional()
        .matches(/^[0-9+]*$/, 'Only numbers and + are allowed'),
    remarks: Yup.string().trim().optional(),
    // Liner

    linerRemarks: Yup.string()
        .trim()
        .when('actionType', {
            is: 'R',
            then: (schema) => schema.required('Liner Remarks is required'),
            otherwise: (schema) => schema.optional(),
        }),
    actionType: Yup.string().optional(),
});

const InvoiceRequest = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const dataCountry = useDataCountry();
    const onBack = useBackNavigation();
    const { blNum, encryptdoc } = useParams();
    const oblNumber = useSelector((state: RootState) => state.oblDetails.oblForm.number);
    const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
    const rbac = useSelector((state: RootState) => state.userProfile);
    const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
    const docAttachment = useSelector((state: RootState) => state.invoiceRequest.customerAttData);
    const selectedAttachmentName = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerAttachmentName);
    const selectedAttachmentFilePath = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerAttachmentFilePath);
    const serverFilePath = useSelector((state: RootState) => state.invoiceRequest.selectedCustomerFileServerPath);
    const invoiceRequestViewForm = useSelector((state: RootState) => state.invoiceRequest.invoiceRequestFormView);
    const invoiceTypeList = useSelector((state: RootState) => state.invoiceRequest.invoiceTypeList);
    const validatedBlDetails = useSelector((state: RootState) => state.invoiceRequest.validatedBlDetails);
    const containerData = useSelector((state: RootState) => state.invoiceRequestContainer.data);
    const extensionTypeOption = useSelector(
        (state: RootState) => state.invoiceRequestContainer.extensionTypeOptions
    );
    const instructionList = useSelector((state: RootState) => state.documentHistory.data);
    const loginPartnerId = useSelector((state: RootState) => state.userProfile?.profile?.partnerId);
    const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || '';

    const matchPth = [
        {
            path: `${APP_PATH_PREFIX}/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[3] !== 'instruction',
        },
        {
            path: `${APP_PATH_PREFIX}/instruction/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[4] !== 'instruction',
        },
        {
            path: `${APP_PATH_PREFIX}/payment/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[4] !== 'instruction',
        },
        { path: `${APP_PATH_PREFIX}/instruction/:blNum/:encrypt/:encryptdoc` },
    ].some(({ path, condition }: { path: string; condition?: boolean }) => {
        return (
            (condition ?? true) &&
            matchPath({ path, caseSensitive: true, end: true }, location.pathname)
        );
    });

    const isInstructionDashboardNewForm = !blNum;

    const [blCountry, setBlCountry] = useBlCountry(
        matchPth ? 'view' : 'new',
        'import',
        822
    );
    const [invoiceReqBlCountry] = useInvoiceRequestBlCountry(
        matchPth ? 'view' : 'new',
        'import',
        822
    );
    const activeBlCountry = isInstructionDashboardNewForm ? invoiceReqBlCountry : blCountry;

    const showCurrency =
        activeBlCountry == 'CI';

    const linerId =
        profileDetails?.partnerType === 'liner'
            ? profileDetails?.partnerId
            : selectedLinerId;

    const [disableFields, setDisableFields] = useState(
        profileDetails?.partnerType === 'liner' ||
        (!!matchPth && profileDetails?.partnerType !== 'liner')
    );

    const recentId = useSelector((state: RootState) => {
        const arr = state?.documentHistory?.data?.filter(
            (inst) =>
                inst.instructionId == 822 &&
                inst.event == 'Submitted' && (inst.partnerId == null || inst.partnerId == loginPartnerId)
        );
        const recentInst = arr.length <= 0 ? null :
            arr.reduce((a: any, b: any) => (a.blEventId > b.blEventId ? a : b))

        return ['Y', '1', '2', '3', '4', '5', '6'].includes(recentInst?.docStatus ?? '')
            ? null
            : recentInst;
    });

    const [typeOfDocumentList, setTypeOfDocumentList] = useState<MstOption[]>([]);
    const [customsChannelList, setCustomsChannelList] = useState<MstOption[]>([]);
    const [paymentModeList, setPaymentModeList] = useState<MstOption[]>([]);
    const [deliveryZoneList, setDeliveryZoneList] = useState<MstOption[]>([]);
    const [deliveryLocationList, setDeliveryLocationList] = useState<MstOption[]>([]);
    const [typeOfTrailerList, setTypeOfTrailerList] = useState<MstOption[]>([]);
    const [modeOfTransportList, setModeOfTransportList] = useState<MstOption[]>([]);
    const [customerLoadingFileState, setCustomerLoadingFileState] = useState<Record<string, any>>({});

    const isDemurrageDetentionInvoiceSelected = invoiceTypeList?.some(
        (item) =>
            item.allowAccess &&
            String(item.value) === String(DEMURRAGE_DETENTION_INVOICE_TYPE_CODE)
    );

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        clearErrors,
        resetField,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(validationSchema) as any,
        context: { isDemurrageDetentionInvoiceSelected },
        defaultValues: {
            blNumber: '',
            typeOfDocument: '',
            customsClearanceChannel: '',
            partyToInvoice: '',
            paymentMode: '',
            emailAddress: '',
            contactNumber: '',
            remarks: '',
            deliveryLocation: '',
            linerRemarks: '',
            actionType: '',
        },
    });

    const blNumber = watch('blNumber');

    const decryptedBlId = AESDecrypt(blNum ?? '');

    const validatedBlId = validatedBlDetails?.blId != null ? String(validatedBlDetails.blId) : null;
    const effectiveBlId = isInstructionDashboardNewForm ? validatedBlId : decryptedBlId;

    const isBlNumberValid = isInstructionDashboardNewForm
        ? !!validatedBlId
        : !!decryptedBlId;

    const isExistingInvoiceRequest =
        !isInstructionDashboardNewForm && (!!encryptdoc || !!recentId?.docId);

    // const displayNumber = oblNumber ? String(oblNumber) : '';

    const isCustomer = profileDetails?.partnerType === 'customer';
    const isLiner = profileDetails?.partnerType === 'liner';

    const statusLabel = invoiceRequestViewForm?.status ?? '';
    const isSubmitted = statusLabel === INVOICE_REQUEST_STATUS_LABEL.SUBMITTED;
    const isCompleted = statusLabel === INVOICE_REQUEST_STATUS_LABEL.COMPLETED;
    const isRejected =
        statusLabel === INVOICE_REQUEST_STATUS_LABEL.REJECTED ||
        (statusLabel ?? '').includes(INVOICE_REQUEST_STATUS_LABEL.REJECTED);

    const hasApproveRejectButtonAccess =
        rbac?.rbacDetails?.invoiceRequestApproveReject === 'Y';

    const showApproveRejectButtons =
        isLiner &&
        dataCountry === activeBlCountry &&
        hasApproveRejectButtonAccess &&
        !(instructionList
            ?.filter((v: any) => v?.docId === invoiceRequestViewForm?.invoiceRequestId)
            ?.map(
                (u: any) =>
                    u?.event === INVOICE_REQUEST_STATUS_LABEL.COMPLETED ||
                    (u?.event ?? '').includes(INVOICE_REQUEST_STATUS_LABEL.REJECTED)
            )
            ?.includes(true)) &&
        !isCompleted &&
        !isRejected;


    const approveRejectButtonsDisabled =
        isCompleted || isRejected ? disableFields : false;

    const disableLinerReviewFields =
        profileDetails?.partnerType != 'liner' ||
        !hasApproveRejectButtonAccess ||
        isCompleted ||
        isRejected ||
        instructionList
            ?.filter((v: any) => v?.docId === invoiceRequestViewForm?.invoiceRequestId)
            ?.map((u: any) => u?.event === 'Completed' || u?.event === 'Refunded')
            ?.includes(true);

    const showLinerRemarks = isLiner || (isCustomer && (isCompleted || isRejected));
    const showDeliveryLocation =  (isCustomer && isCompleted);

    useEffect(() => {
        if (!activeBlCountry || !linerId) return;

        dispatch(
            getInvoiceRequestTypeOfDocumentList({ dataCountry: activeBlCountry, linerId: linerId })
        )
            .unwrap()
            .then((res: any) => setTypeOfDocumentList(res ?? []))
            .catch(() => setTypeOfDocumentList([]));

        dispatch(
            getInvoiceRequestCustomsClearanceChannelList({
                dataCountry: activeBlCountry,
                linerId: linerId,
            })
        )
            .unwrap()
            .then((res: any) => setCustomsChannelList(res ?? []))
            .catch(() => setCustomsChannelList([]));

        dispatch(getInvoiceRequestPaymentModeList({ dataCountry: activeBlCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setPaymentModeList(res ?? []))
            .catch(() => setPaymentModeList([]));

        dispatch(getInvoiceRequestDeliveryZoneList({ countryCode: activeBlCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setDeliveryZoneList(res ?? []))
            .catch(() => setDeliveryZoneList([]));

        dispatch(getInvoiceRequestModeOfTransportList({ dataCountry: activeBlCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setModeOfTransportList(res ?? []))
            .catch(() => setModeOfTransportList([]));

        dispatch(getInvoiceRequestInvChangeTypeList({ dataCountry: activeBlCountry, linerId: linerId }));


        dispatch(getInvoiceRequestDeliveryLocationList({ dataCountry: activeBlCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setDeliveryLocationList(res ?? []))
            .catch(() => setDeliveryLocationList([]));


        dispatch(getTypeofTrailerList({ dataCountry: activeBlCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setTypeOfTrailerList(res ?? []))
            .catch(() => setTypeOfTrailerList([]));
        


    }, [linerId, activeBlCountry]);

   

    useEffect(() => {
        if (!isInstructionDashboardNewForm && oblNumber) {
        setValue('blNumber', String(oblNumber), { shouldValidate: true });
        clearErrors('blNumber');
        }
    }, [oblNumber, isInstructionDashboardNewForm, setValue, clearErrors]);

    useEffect(() => {
        if (isExistingInvoiceRequest) return;
        if (!isBlNumberValid || !effectiveBlId) return;

        const effectiveLinerId =
            profileDetails?.partnerType === 'liner'
                ? profileDetails?.partnerId
                : selectedLinerId;
        if (!effectiveLinerId) return;

        dispatch(
            getOBLForInvoiceRequest({
                blNumber: effectiveBlId,
                selectedLinerId: effectiveLinerId,
            })
        );
    }, [
        isExistingInvoiceRequest,
        isBlNumberValid,
        effectiveBlId,
        selectedLinerId,
        profileDetails?.partnerType,
        profileDetails?.partnerId
    ]);

    useEffect(() => {
        setCustomerLoadingFileState({});
        dispatch(clearCustomerFields());

        if (activeBlCountry && linerId && !encryptdoc && !recentId) {
            dispatch(
                getInvoiceRequestCustomerAttConf({
                    country: activeBlCountry,
                    liner: linerId,
                })
            );
        }
    }, [activeBlCountry, linerId, encryptdoc, recentId, location.pathname, dispatch]);

    // useEffect(() => {
    //     if (!linerId) {
    //         setZoneBasedTransportList([]);
    //         return;
    //     }
    //
    //     if (isDelZ1ToZ4) {
    //         dispatch(
    //             getInvoiceRequestZoneBasedTransportList({ dataCountry: dataCountry, linerId: linerId })
    //         )
    //             .unwrap()
    //             .then(setZoneBasedTransportList)
    //             .catch(() => setZoneBasedTransportList([]));
    //         return;
    //     }
    //
    //     setZoneBasedTransportList([]);
    // }, [deliveryZone, linerId, isDelZ1ToZ4]);

    useEffect(() => {
        if (isInstructionDashboardNewForm) return;

        dispatch(clearInvoiceRequestViewAndCustomerState());
        let payload: { invoiceRequestId: any; country: string; linerId: any } | null = null;
        if (encryptdoc && linerId && activeBlCountry) {
            payload = {
                invoiceRequestId: AESDecrypt(encryptdoc),
                country: activeBlCountry,
                linerId: linerId,
            };
        } else if (!encryptdoc && recentId?.docId && linerId && activeBlCountry) {
            payload = {
                invoiceRequestId: recentId?.docId,
                country: activeBlCountry,
                linerId: linerId,
            };
        }
        //console.log("payload",payload);
        if (!payload) return;

        dispatch(getInvoiceRequestDetails(payload))
            .unwrap()
            .then((res: any) => {
                const form = res?.data ?? {};
                setValue('blNumber', form?.blNo ?? '');
                setValue('typeOfDocument', form?.typeOfDocument ?? '');
                setValue('customsClearanceChannel', form?.customsClearanceChannel ?? '');
                setValue('partyToInvoice', form?.partyToInvoice ?? '');
                setValue('paymentMode', form?.methodOfPayment ?? '');
                setValue('emailAddress', form?.emailAddress ?? '');
                setValue('contactNumber', form?.contactNumber ?? '');
                setValue('remarks', form?.remarks ?? '');
                setValue('deliveryLocation', form?.deliveryLocation ?? '');
                setValue('linerRemarks', form?.linerRemarks ?? '');

                if (Array.isArray(form?.attList)) {
                    if (form.attList.length > 0) {
                        dispatch(updateCustomerAttData(form.attList));
                    }

                    const nameByKey: Record<string, any> = {};
                    const filePathByKey: Record<string, any> = {};

                    for (let i = 0; i < form.attList.length; i++) {
                        const key =
                            form.attList[i]?.attachmentId ?? form.attList[i]?.rowNumber ?? null;
                        if (key == null) continue;

                        nameByKey[key] = { name: form.attList[i]?.fileName || null };
                        filePathByKey[key] = form.attList[i]?.filePath || null;
                    }

                    dispatch(updateSelectedCustomerAttachmentName(nameByKey));
                    dispatch(updateSelectedCustomerAttachmentFilePath(filePathByKey));
                }
            })
            .catch(() => toast.error('Failed to load Invoice Request'));
    }, [encryptdoc, recentId?.docId, activeBlCountry, linerId, isInstructionDashboardNewForm]);


    const isCustomerFileUploadBusy = () =>
        Object.values(customerLoadingFileState).some(
            (val) => val != 100 && val != 0 && val != -1 && val != undefined
        );

    const isInvReqCustDocsIncomplete = (): boolean => {
        const docAttachmentKey = docAttachment;
        const viewFormAttKey = invoiceRequestViewForm?.attList ?? [];

        if (docAttachmentKey?.list?.length === 0) {
            return true;
        }

        return (
            docAttachmentKey.list
                .filter((x: any) => x.m)
                .findIndex((d: any) => {
                    const key = d?.attachmentId ?? d?.rowNumber ?? null;
                    const serverEntry = serverFilePath?.[key];
                    const resolvedServerPath = Array.isArray(serverEntry)
                        ? serverEntry?.[0]
                        : serverEntry;

                    return (
                        !selectedAttachmentName?.[key] ||
                        ((customerLoadingFileState[key] !== 100 || !resolvedServerPath) &&
                            (viewFormAttKey.findIndex(
                                (i: any) => i?.docId == d?.docId && i?.attachmentId
                            ) === -1 ||
                                customerLoadingFileState[key] === -1))
                    );
                }) !== -1
        );
    };

    const validateContainerDetails = () => {
        const data = Array.isArray(containerData) ? containerData : [];

        const hasContainers = data.some(
            (item: any) => String(item.containerNo ?? '').trim() !== ''
        );

        if (!hasContainers) {
            toast.error('No containers found for this BL');
            return false;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const containerErrors = data.flatMap((item: any, index: number) => {
            const errors: string[] = [];
            const deliveryZone = String(item.deliveryZone ?? '').trim();
            const deliveryZoneLabel =
                deliveryZoneList.find((option) => String(option.value) === deliveryZone)?.label ?? '';
            const isQuaySideStripping =
                /quay[- ]?side\s*stripping|d[eé]potage\s*à\s*quai/i.test(deliveryZoneLabel);
            const modeOfTransport = String(item.modeOfTransport ?? '').trim();

            if (!deliveryZone) errors.push('Delivery Zone is required');
            if (!isQuaySideStripping && !String(item.subLocation ?? '').trim()) {
                errors.push('Sub Location is required');
            }
            if (!modeOfTransport) errors.push('Mode of Transport is required');
            if (modeOfTransport === '2' && !String(item.typeOfTrailer ?? '').trim()) {
                errors.push('Type of Trailer is required');
            }

            if (isDemurrageDetentionInvoiceSelected) {
                const needExtensionFor = item.needExtensionFor;
                if (
                    needExtensionFor === '0' ||
                    needExtensionFor === 0 ||
                    needExtensionFor == null ||
                    needExtensionFor === ''
                ) {
                    errors.push('Need Extension For is required');
                }

                if (!item.plannedPickupDate) {
                    errors.push('Planned Delivery Date is required');
                } else {
                    const plannedPickupDate = new Date(item.plannedPickupDate);
                    plannedPickupDate.setHours(0, 0, 0, 0);
                    if (isNaN(plannedPickupDate.getTime())) {
                        errors.push('Planned Delivery Date is invalid');
                    } else if (plannedPickupDate < today) {
                        errors.push('Planned Delivery Date cannot be in the past');
                    }
                }

                if (showCurrency && !String(item.currency ?? '').trim()) {
                    errors.push('Currency is required');
                }
            }

            return errors.length
                ? [`Container ${item.containerNo || index + 1}: ${errors.join(', ')}`]
                : [];
        });

        if (containerErrors.length) {
            toast.error(containerErrors.join('; '));
            return false;
        }

        return true;
    };

    const onSubmitForm = handleSubmit((payload) => {
        if (!invoiceTypeList?.some((item) => item.allowAccess)) {
            toast.error('Select at least one Invoice Type');
            return;
        }

        if (isBlNumberValid && !validateContainerDetails()) {
            return;
        }

        const invoiceRequestId =
            !isSubmitted
                ? null
                : invoiceRequestViewForm?.invoiceRequestId || null;

        const viewFormAttKey = invoiceRequestViewForm?.attList ?? [];
        const invoiceRequestInstructionAtts = (docAttachment?.list ?? [])
            ?.filter((d: any) => {
                const key = d?.attachmentId ?? d?.rowNumber ?? null;
                return customerLoadingFileState[key] != -1;
            })
            ?.map((item: any) => {
                const key = item?.attachmentId ?? item?.rowNumber ?? null;
                const serverEntry = serverFilePath?.[key];
                return {
                    invoiceRequestInstructionAttId: isSubmitted
                        ? viewFormAttKey?.find((x: any) => item?.docId === x?.docId)?.attachmentId ??
                        null
                        : null,
                    docId: item?.docId ?? null,
                    fileName: selectedAttachmentName?.[key]?.name ?? '',
                    filePath: selectedAttachmentFilePath?.[key] ?? null,
                    tempFileName: Array.isArray(serverEntry) ? (serverEntry?.[1] ?? null) : null,
                    tempFilePath: Array.isArray(serverEntry) ? (serverEntry?.[0] ?? null) : null,
                };
            })
            ?.filter((x: any) => x?.fileName);

        const invoiceTypeAccessDtlsList = (invoiceTypeList ?? []).map((item) => {
            const entry: any = {
                type: item.value,
                allowAccess: item.allowAccess ? 'Y' : 'N',
            };

            if (!invoiceRequestId) {
                return entry;
            }

            if (item.invoiceTypeAccessId != null) {
                entry.invoiceTypeAccessId = item.invoiceTypeAccessId;
            }
            entry.invoiceRequestInstructionId =
                item.invoiceRequestInstructionId ?? invoiceRequestId;

            return entry;
        });

        const data = Array.isArray(containerData) ? containerData : [];

        const finalPayload = {
            invoiceRequestId,
            blId: effectiveBlId ? Number(effectiveBlId) : null,
            linerId: Number(selectedLinerId),
            countryCode: activeBlCountry ?? '',
            typeOfDocument: payload.typeOfDocument ?? '',
            customsClearanceChannel: payload.customsClearanceChannel ?? '',
            partyToInvoice: payload.partyToInvoice ?? '',
            methodOfPayment: payload.paymentMode ?? '',
            emailAddress: payload.emailAddress ?? '',
            contactNumber: payload.contactNumber ?? '',
            remarks: payload.remarks ?? '',
            isDemurrageDetentionInvType: isDemurrageDetentionInvoiceSelected,
            invoiceRequestInstructionAtts: invoiceRequestInstructionAtts ?? [],
            invoiceTypeAccessDtlsList,

            containerList: data
                .map((item: any) => {
                    const entry: any = {
                        containerNo: item.containerNo,
                        deliveryZoneId: item.deliveryZone ? Number(item.deliveryZone) : null,
                        subLocationId: item.subLocation ? Number(item.subLocation) : null,
                        deliveryZoneName:
                            item.deliveryZoneName ||
                            deliveryZoneList.find(
                                (option) => String(option.value) === String(item.deliveryZone)
                            )?.label ||
                            '',
                        subLocationName: item.subLocationName || '',
                        modeOfTransport: item.modeOfTransport
                            ? Number(item.modeOfTransport)
                            : null,
                        typeOfTrailer: item.typeOfTrailer || '',
                        isHazardous: !!item.isHazardous,
                        pickupLocation:
                            isDemurrageDetentionInvoiceSelected &&
                            profileDetails.partnerCountry == 'CI' && item.needExtensionFor == 0
                                ? extensionTypeOption[0].value
                                : isDemurrageDetentionInvoiceSelected
                                    ? item.needExtensionFor
                                    : null,
                        pickupDate:
                            isDemurrageDetentionInvoiceSelected &&
                            item.plannedPickupDate && item.needExtensionFor != 0
                                ? moment(item.plannedPickupDate).format('YYYY-MM-DD')
                                : null,
                        currency:
                            isDemurrageDetentionInvoiceSelected && showCurrency
                                ? item.currency
                                : null,
                    };

                    if (invoiceRequestId) {
                        entry.invoiceRequestContainerId = item.invoiceRequestContainerId ?? null;
                        entry.invoiceRequestInstructionId =
                            item.invoiceRequestInstructionId ?? invoiceRequestId;
                    }

                    return entry;
                }),
        };

       // console.log('Invoice Request submit payload:', finalPayload);

        dispatch(submitInvoiceRequest(finalPayload))
            .unwrap()
            .then((result: any) => {
                toast.success(result?.responseMsg);
                dispatch(updateDashbaordRefresh(true));
                setTimeout(() => {
                    dispatch(
                        getInstructionList({
                            blNum: AESDecrypt(blNum ?? ''),
                            country: activeBlCountry,
                        })
                    );
                }, 3000);
                onBack();
            })
            .catch((e: any) =>
                toast.error(
                    e?.responseMsg || 'Error while submitting Invoice Request'
                )
            );
    });

    const handleInvoiceTypeChange = (code: number | string, checked: boolean) => {
        if (String(code) === String(DEMURRAGE_DETENTION_INVOICE_TYPE_CODE)) {
            if (!checked) {
                dispatch(clearTableInputValues());
            }
        }
        dispatch(
            updateInvoiceType({
                code,
                key: 'allowAccess',
                value: checked,
            })
        );
    };

    const handleReset = () => {
        if (isInstructionDashboardNewForm) {
            setValue('blNumber', '');
            clearErrors('blNumber');
            
        }
        dispatch(clearValidatedBlDetails());
        setValue('typeOfDocument', '');
        setValue('customsClearanceChannel', '');
        setValue('partyToInvoice', '');
        setValue('paymentMode', '');
        dispatch(resetInvoiceTypeList());
        setValue('emailAddress', '');
        setValue('contactNumber', '');
        setValue('remarks', '');
        dispatch(clearTableInputValues());
        dispatch(updateSelectedDeliveryZone({ value: '', label: '' }));
        setCustomerLoadingFileState({});
        dispatch(updateSelectedCustomerAttachmentName({}));
        dispatch(updateSelectedCustomerAttachmentFilePath({}));
        dispatch(updateSelectedCustomerFileServerPath({}));
        clearErrors();
    };

    const handleBlNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (!isInstructionDashboardNewForm || disableFields) return;

        const trimmed = e.target.value.trim();
        const validatedBlNo = String(validatedBlDetails?.blNo ?? '').trim();

        if (trimmed === validatedBlNo) return;

        handleReset();

        if (!trimmed) return;

        setValue('blNumber', trimmed, { shouldValidate: true });
        dispatch(validateBlNumber(trimmed))
            .unwrap()
            .then((res) => {
                if (!res?.blId) {
                    handleReset();
                    toast.error('No BL found');
                    return;
                }

                if (res?.invoiceRequestInstructionId) {
                    handleReset();
                    toast.error(
                        'Invoice Request instruction cannot be initiated for this B/L.'
                    );
                }
            })
            .catch(() => {
                handleReset();
                toast.error('No BL found');
            });
    };

    // Instruction dashboard new form (/instruction/{822}): clear on every + instruct open
    useEffect(() => {
        if (!isInstructionDashboardNewForm) return;

        dispatch(clearDocumentHistory());
        dispatch(updateSelectedDocIdDH(null));
        dispatch(updateSelectedEventIdDH(null));
        dispatch(clearInvoiceRequestViewAndCustomerState());
        dispatch(clearValidatedBlDetails());
        dispatch(clearData());
        handleReset();

    }, [location.pathname]);

    const handleApprove = async () => {
        //alert("test");
        setValue('actionType', 'A');
        clearErrors(['linerRemarks']);
       // const isValid = await trigger(['deliveryLocation']);
        //if (!isValid) return;

        const payload = {
            invoiceRequestId:
                invoiceRequestViewForm?.invoiceRequestId ??
                (encryptdoc ? AESDecrypt(encryptdoc) : null),
            blId: decryptedBlId ? Number(decryptedBlId) : null,
            linerId: Number(selectedLinerId),
            countryCode: activeBlCountry ?? '',
            status: 'Completed',
            deliveryLocation: watch('deliveryLocation') ?? '',
            linerRemarks: watch('linerRemarks') ?? '',
        };

       // console.log('Invoice Request approve payload:', payload);

        dispatch(approveRejectInvoiceRequest(payload))
            .unwrap()
            .then((result: any) => {
                toast.success(result?.responseMsg);
                dispatch(updateDashbaordRefresh(true));
                setTimeout(() => {
                    dispatch(
                        getInstructionList({
                            blNum: AESDecrypt(blNum ?? ''),
                            country: activeBlCountry,
                        })
                    );
                }, 3000);
                onBack();
            })
            .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    };

    const handleReject = async () => {
        setValue('actionType', 'R');
        resetField('deliveryLocation', { defaultValue: '' });
        clearErrors(['deliveryLocation']);
        const isValid = await trigger(['linerRemarks']);
        if (!isValid) return;

        setValue('deliveryLocation', '');

        const payload = {
            invoiceRequestId:
                invoiceRequestViewForm?.invoiceRequestId ??
                (encryptdoc ? AESDecrypt(encryptdoc) : null),
            blId: decryptedBlId ? Number(decryptedBlId) : null,
            linerId: Number(selectedLinerId),
            countryCode: activeBlCountry ?? '',
            status: 'Rejected',
            deliveryLocation: '',
            linerRemarks: watch('linerRemarks') ?? '',
        };

       // console.log('Invoice Request reject payload:', payload);

        dispatch(approveRejectInvoiceRequest(payload))
            .unwrap()
            .then((result: any) => {
                toast.success(result?.responseMsg);
                dispatch(updateDashbaordRefresh(true));
                setTimeout(() => {
                    dispatch(
                        getInstructionList({
                            blNum: AESDecrypt(blNum ?? ''),
                            country: activeBlCountry,
                        })
                    );
                }, 3000);
                onBack();
            })
            .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    };

    return (
        <Card className='h-100 overflow-auto'>
            <Card.Header className='d-flex align-items-center border-bottom sticky-top bg-white'>
                <FontAwesomeIcon
                    icon={faArrowAltCircleLeft}
                    className='text-warning cursor-pointer fa-lg me-1 mb-2'
                    onClick={onBack}
                />
                <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
                    <div className='d-flex align-items-center gap-5 mb-1 flex-wrap'>
                        <h2 className='fs-0 text-primary mb-0'>{t('resource_2:invoiceRequest')}</h2>
                    </div>
                </div>
            </Card.Header>

            <Card.Body>
                <Form onSubmit={onSubmitForm} autoComplete='off'>
                    <div className='row'>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1'>{t('resource_2:bl_no')}</Form.Label>
                            <Form.Control
                                className={`fs--1 ${errors.blNumber ? 'is-invalid' : ''}`}
                                disabled={!isInstructionDashboardNewForm || disableFields}
                                maxLength={50}
                                {...register('blNumber', {
                                    onBlur: handleBlNumberBlur,
                                })}
                            />
                            <div className='invalid-feedback d-block'>
                                {errors.blNumber?.message?.toString()}
                            </div>
                        </div>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1'>{t('resource_3:typeOfDocument')}</Form.Label>
                            <Form.Select
                                value={watch('typeOfDocument')}
                                {...register('typeOfDocument')}
                                disabled={disableFields}
                                className={`fs--1 ${errors.typeOfDocument ? 'is-invalid' : ''}`}
                            >
                                <option value=''>Select</option>
                                {typeOfDocumentList?.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <div className='invalid-feedback d-block'>
                                {errors.typeOfDocument?.message?.toString()}
                            </div>
                        </div>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1'>{t('resource_3:customsClearanceChannel')}</Form.Label>
                            <Form.Select
                                value={watch('customsClearanceChannel')}
                                {...register('customsClearanceChannel')}
                                disabled={disableFields}
                                className={`fs--1 ${errors.customsClearanceChannel ? 'is-invalid' : ''}`}
                            >
                                <option value=''>Select</option>
                                {customsChannelList?.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <div className='invalid-feedback d-block'>
                                {errors.customsClearanceChannel?.message?.toString()}
                            </div>
                        </div>

                    </div>

                    <div className='row'>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1'>{t('resource_3:partyToInvoice')}</Form.Label>
                            <Form.Control
                                className={`fs--1 ${errors.partyToInvoice ? 'is-invalid' : ''}`}
                                disabled={disableFields}
                                maxLength={100}
                                {...register('partyToInvoice')}
                                onBlur={(e) => setValue('partyToInvoice', e.target.value.trim())}
                            />
                            <div className='invalid-feedback d-block'>
                                {errors.partyToInvoice?.message?.toString()}
                            </div>
                        </div>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1'>{t('resource_5:paymentModeOnInvoiceForm')}</Form.Label>
                            <Form.Select
                                value={watch('paymentMode')}
                                {...register('paymentMode')}
                                disabled={disableFields}
                                className={`fs--1 ${errors.paymentMode ? 'is-invalid' : ''}`}
                            >
                                <option value=''>Select</option>
                                {paymentModeList?.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <div className='invalid-feedback d-block'>
                                {errors.paymentMode?.message?.toString()}
                            </div>
                        </div>
                        <div className='col-lg-4 mb-2'>
                            <span className='mandatory'>*</span>
                            <Form.Label className='fs--1 fw-medium'>{t('resource_2:emailAddress')}</Form.Label>
                            <Form.Control
                                type='email'
                                className={`fs--1 ${errors.emailAddress ? 'is-invalid' : ''}`}
                                disabled={disableFields}
                                maxLength={100}
                                {...register('emailAddress')}
                                onBlur={(e) => setValue('emailAddress', e.target.value.trim())}
                            />
                            <div className='invalid-feedback d-block'>
                                {errors.emailAddress?.message?.toString()}
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-lg-4 mb-3'>
                            <Form.Label className='fs--1 fw-medium'>{t('resource_2:contactNo')}</Form.Label>
                           
                            <Form.Control
                                type='text'
                                maxLength={20}
                                disabled={disableFields}
                                {...register('contactNumber')}
                                className={`fs--1 ${errors.contactNumber ? 'is-invalid' : ''}`}
                                onInput={(e: React.FormEvent<HTMLInputElement>) =>
                                    setValue(
                                        'contactNumber',
                                        e.currentTarget.value.replace(/[^0-9+]/g, ''),
                                        { shouldValidate: true }
                                    )
                                }
                                onBlur={(e) =>
                                    setValue('contactNumber', e.target.value.trim(), {
                                        shouldValidate: true,
                                    })
                                }
                            />
                            <div className='invalid-feedback d-block'>
                                {errors.contactNumber?.message?.toString()}
                            </div>
                        </div>
                        <div className='col-lg-8 mb-3'>
                            <Form.Label className='fs--1 fw-medium'>{t('resource_1:remark')}</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={2}
                                maxLength={100}
                                disabled={disableFields}
                                className={`fs--1 ${errors.remarks ? 'is-invalid' : ''}`}
                                {...register('remarks')}
                                onBlur={(e) => setValue('remarks', e.target.value.trim())}
                            />
                            <div className='invalid-feedback d-block'>
                                {errors.remarks?.message?.toString()}
                            </div>
                        </div>
                    </div>

                    {(invoiceTypeList?.length ?? 0) > 0 && (
                    <div className='row mt-2 mb-3'>
                        <div className='col-lg-12'>
                            <Form.Label className='fs--1 d-block'>
                                <span className='mandatory me-1'>*</span>{t('resource_2:invoiceType')}
                            </Form.Label>
                            {invoiceTypeList?.map((item) => (
                                <Form.Check
                                    key={item.value}
                                    inline
                                    type='checkbox'
                                    id={`inv-type-${item.value}`}
                                    className='fs--1 me-3 mb-1'
                                    label={item.text ?? item.label}
                                    disabled={disableFields}
                                    checked={item.allowAccess}
                                    onChange={({ target }) =>
                                        handleInvoiceTypeChange(item.value, target.checked)
                                    }
                                />
                            ))}
                        </div>
                    </div>
                    )}

                    {isBlNumberValid && (
                        <div className='row'>
                            <InvoiceRequestContainerDetails
                                disableFields={disableFields}
                                isDemurrageDetentionSelected={isDemurrageDetentionInvoiceSelected}
                                disabledPath={matchPth}
                                country={activeBlCountry}
                                deliveryZoneList={deliveryZoneList}
                                modeOfTransportList={modeOfTransportList}
                                typeOfTrailerList={typeOfTrailerList}
                                blId={
                                    isInstructionDashboardNewForm
                                        ? validatedBlId ?? ''
                                        : decryptedBlId
                                }
                            />
                        </div>
                    )}

                    <div className='mb-4'>
                        <InvoiceRequestCustomerDocuments
                            isViewMode={disableFields}
                            customerLoadingFileState={customerLoadingFileState}
                            setCustomerLoadingFileState={setCustomerLoadingFileState}
                        />
                    </div>


                    <div className='row'>
                         {/* {showDeliveryLocation && ( 
                            <div className='col-lg-4 mb-2'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'>{t('resource_2:deliveryLocation')}</Form.Label>
                                <Form.Select
                                    value={watch('deliveryLocation')}
                                    {...register('deliveryLocation')}
                                    disabled={
                                        isCustomer || disableLinerReviewFields
                                    }
                                    className={`fs--1 ${errors.deliveryLocation ? 'is-invalid' : ''}`}
                                >
                                    <option value=''>Select</option>
                                    {deliveryLocationList?.map((item) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div className='invalid-feedback d-block'>
                                    {errors.deliveryLocation?.message?.toString()}
                                </div>
                            </div>
                         )}  */}
                        {showLinerRemarks && (

                            <div className='col-lg-6 mb-3'>
                                <Form.Label className='fs--1 fw-medium'>{t('resource_2:linerremark')}</Form.Label>
                                <Form.Control
                                    as='textarea'
                                    rows={2}
                                    maxLength={100}
                                    disabled={
                                        isCustomer || disableLinerReviewFields
                                    }
                                    className={`fs--1 ${errors.linerRemarks ? ' is-invalid' : ''}`}
                                    {...register('linerRemarks')}
                                    onBlur={(e) => setValue('linerRemarks', e.target.value.trim())}
                                />
                                <div className='invalid-feedback d-block'>
                                    {errors.linerRemarks?.message?.toString()}
                                </div>
                            </div>
                        )}
                        {/* )} */}
                    </div>

                    {(isSubmitted || isRejected) &&
                        profileDetails?.partnerType != 'liner' &&
                        disableFields &&
                        invoiceRequestViewForm?.requestedPartnerId ==
                        profileDetails?.partnerId && (
                            <div className='d-flex justify-content-center align-items-center mb-3'>
                                <Button
                                    variant='falcon-primary'
                                    size='sm'
                                    className='me-2'
                                    onClick={() => setDisableFields(false)}
                                >
                                    <FontAwesomeIcon icon={faEdit} className='me-2' />
                                    {t('resource_1:edit')}
                                </Button>
                            </div>
                        )}

                    <div className='d-flex justify-content-center align-items-center gap-3 mt-3 flex-wrap'>
                        {showApproveRejectButtons && (
                            <Button
                                variant='success'
                                size='sm'
                                disabled={approveRejectButtonsDisabled}
                                onClick={handleApprove}
                            >
                                <FontAwesomeIcon icon={faCheck} className='me-2' /> Approve
                            </Button>
                        )}
                        {showApproveRejectButtons && (
                            <Button
                                variant='danger'
                                size='sm'
                                disabled={approveRejectButtonsDisabled}
                                onClick={handleReject}
                            >
                                <FontAwesomeIcon icon={faTimes} className='me-2' /> Reject
                            </Button>
                        )}
                        {isCustomer && !disableFields && dataCountry === activeBlCountry && (
                            <Button
                                variant='falcon-primary'
                                size='sm'
                                onClick={onSubmitForm}
                                disabled={
                                    isCustomerFileUploadBusy() || isInvReqCustDocsIncomplete()
                                }
                            >
                                <FontAwesomeIcon icon={faSave} className='me-2' />
                                Submit
                            </Button>
                        )}
                        {isCustomer && !disableFields && dataCountry === activeBlCountry && (
                            <Button variant='falcon-primary' size='sm' onClick={handleReset}>
                                <FontAwesomeIcon icon={faRedo} className='me-2' />
                                Reset
                            </Button>
                        )}

                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default InvoiceRequest;
